import * as React from 'react';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { ImageType, IOrderRow } from '../../../Entities';
import { useArchiveCheckQuery } from '../../archiving/queries/use-query-archive-status';
import {
    isProcessingImages,
    PROCESSING_POLL_INTERVAL_MILLISECONDS,
    useImagesQuery
} from '../../listImages/queries/use-query-images';
import { ImageEditorPhotoStatus, usePhotoStatusQuery } from '../queries/use-query-photo-status';
import { canViewUneditedImages } from './utils';

export enum ImageProcessingStatus {
    Loading = 'loading',
    Archived = 'archived',
    Ready = 'ready'
}

type LoadingState = {
    status: ImageProcessingStatus.Loading;
};

type ArchivedState = {
    status: ImageProcessingStatus.Archived;
    onRetrieve: () => void;
};

type ReadyState = {
    status: ImageProcessingStatus.Ready;
    photoStatus: ImageEditorPhotoStatus;
    uneditedOrderRows: IOrderRow[] | null;
    editedOrderRows: IOrderRow[];
    editedOrderRowsUpdatedAt: number;
};

export type ImageEditorImageProcessingDataState = LoadingState | ArchivedState | ReadyState;

type UseImageEditorImageProcessingDataArgs = {
    orderId: number;
    user: AuthenticatedUser;
};

export function useImageEditorImageProcessingData({
    orderId,
    user
}: UseImageEditorImageProcessingDataArgs): ImageEditorImageProcessingDataState {
    let { isPreArchived, isLoading: isArchiveLoading } = useArchiveCheckQuery({ orderId, user });

    let [fetchImagesOverride, setFetchImagesOverride] = React.useState(false);
    let shouldFetchImages = !isArchiveLoading && (!isPreArchived || fetchImagesOverride);

    let editedImagesQuery = useImagesQuery({
        orderId,
        imageType: ImageType.EDITED,
        user,
        enabled: shouldFetchImages
    });

    let isProcessingEditedImages =
        editedImagesQuery.data != null && isProcessingImages(editedImagesQuery.data);

    let photoStatusQuery = usePhotoStatusQuery({
        orderId,
        user,
        enabled: shouldFetchImages,
        refetchInterval: isProcessingEditedImages ? PROCESSING_POLL_INTERVAL_MILLISECONDS : false
    });

    let showUneditedImages =
        photoStatusQuery.data != null && canViewUneditedImages(user, photoStatusQuery.data);

    let uneditedImagesQuery = useImagesQuery({
        orderId,
        imageType: ImageType.UNEDITED,
        user,
        enabled: shouldFetchImages && showUneditedImages
    });

    if (isArchiveLoading) {
        return { status: ImageProcessingStatus.Loading };
    }

    if (isPreArchived && !fetchImagesOverride) {
        return {
            status: ImageProcessingStatus.Archived,
            onRetrieve: function handleRetrieve() {
                setFetchImagesOverride(true);
            }
        };
    }

    let isWaitingForUneditedImages = showUneditedImages && uneditedImagesQuery.isLoading;

    if (photoStatusQuery.isLoading || editedImagesQuery.isLoading || isWaitingForUneditedImages) {
        return { status: ImageProcessingStatus.Loading };
    }

    return {
        status: ImageProcessingStatus.Ready,
        photoStatus: photoStatusQuery.data,
        uneditedOrderRows: showUneditedImages ? uneditedImagesQuery.data : null,
        editedOrderRows: editedImagesQuery.data,
        editedOrderRowsUpdatedAt: editedImagesQuery.dataUpdatedAt
    };
}
